/**
 * 翻译存储服务
 * 实现ITextSelectionTranslationService接口，通过TranslationStorageProvider提供翻译存储功能
 *
 * 核心功能：
 * 1. 连接到底层存储提供者
 * 2. 实现业务逻辑
 * 3. 提供翻译记录管理
 * 4. 处理生词本功能
 */
import { TranslationStorageProvider, generateCacheKey, AVAILABLE_MODELS, } from '@readsavor/utils';
import { LoggerService } from '../../logging/services/logger.service.js';
/**
 * 翻译存储服务
 * 实现翻译记录的存储、检索和更新的业务逻辑
 */
export class TranslationStorageService {
    /**
     * 私有构造函数
     */
    constructor() {
        this.initialized = false;
        this.initPromise = null;
        // 获取日志服务
        this.logger = LoggerService.getInstance();
        // 获取存储提供者实例
        this.provider = TranslationStorageProvider.getInstance();
    }
    /**
     * 获取单例实例
     */
    static getInstance() {
        if (!TranslationStorageService.instance) {
            TranslationStorageService.instance = new TranslationStorageService();
        }
        return TranslationStorageService.instance;
    }
    /**
     * 初始化服务
     * 确保提供者就绪
     */
    async initialize() {
        if (this.initialized)
            return;
        if (this.initPromise)
            return this.initPromise;
        this.initPromise = (async () => {
            try {
                await this.provider.initialize();
                this.initialized = true;
            }
            catch (error) {
                this.initialized = false;
                throw error;
            }
            finally {
                this.initPromise = null;
            }
        })();
        return this.initPromise;
    }
    /**
     * 确保服务已初始化
     */
    async ensureInitialized() {
        if (!this.initialized) {
            await this.initialize();
        }
    }
    /**
     * 保存或更新翻译记录。
     * 此方法接收一个包含业务ID（id）的记录，并将其传递给底层提供者。
     * 底层提供者负责处理"创建或更新"的逻辑（put操作）。
     * @param translation 包含业务ID的翻译记录
     * @returns 保存或更新后的记录的数字顺序ID（orderId）
     */
    async saveTranslation(translation) {
        await this.ensureInitialized();
        return await this.provider.transaction(async () => {
            // The translation record now comes with the correct cache key already generated
            // by HistoryProvider to ensure consistency
            const modelId = translation.provider;
            // Type guard to ensure modelId is a key of AVAILABLE_MODELS
            const isValidModelId = (id) => {
                return id in AVAILABLE_MODELS;
            };
            let displayName = modelId;
            if (isValidModelId(modelId)) {
                displayName = AVAILABLE_MODELS[modelId]?.name || modelId;
            }
            const translationWithDisplayName = {
                ...translation,
                providerDisplayName: displayName,
            };
            const orderId = await this.provider.saveTranslation(translationWithDisplayName);
            return orderId;
        });
    }
    /**
     * 获取特定位置的翻译记录
     */
    async getTranslation(identity, selection) {
        await this.ensureInitialized();
        const id = generateCacheKey(selection, identity);
        // Assuming the provider will have a method to get by the unique string ID
        return await this.provider.getTranslationById(id);
    }
    /**
     * 获取段落的所有翻译记录
     */
    async getTranslationsForParagraph(identity, paragraphId) {
        await this.ensureInitialized();
        return await this.provider.getTranslationsForParagraph(identity, paragraphId);
    }
    /**
     * 获取文档的所有翻译记录
     */
    async getTranslationsForTextDocument(identity) {
        await this.ensureInitialized();
        return await this.provider.getTranslationsForTextDocument(identity);
    }
    /**
     * 删除文档的所有翻译记录
     */
    async deleteTranslationsForTextDocument(identity) {
        await this.ensureInitialized();
        await this.provider.deleteTranslationsForTextDocument(identity);
    }
    /**
     * 获取翻译记录统计信息
     */
    async getStats() {
        await this.ensureInitialized();
        try {
            const count = await this.provider.getTranslationCount();
            return { translationRecordCount: count };
        }
        catch (error) {
            return { translationRecordCount: 0 };
        }
    }
    /**
     * 获取所有词汇记录（所有翻译记录）
     */
    async getAllVocabulary(options) {
        await this.ensureInitialized();
        try {
            return await this.provider.getAllTranslations(options);
        }
        catch (error) {
            return [];
        }
    }
    /**
     * 搜索词汇记录
     */
    async searchVocabulary(searchText, options) {
        await this.ensureInitialized();
        try {
            return await this.provider.searchTranslations(searchText, options);
        }
        catch (error) {
            return [];
        }
    }
    /**
     * 删除词汇记录
     */
    async deleteVocabularyItem(id) {
        await this.ensureInitialized();
        try {
            await this.provider.deleteTranslation(id);
            return true;
        }
        catch (error) {
            return false;
        }
    }
    /**
     * 获取词汇统计信息
     */
    async getVocabularyStats() {
        await this.ensureInitialized();
        try {
            // 获取所有翻译记录
            const allRecords = await this.provider.getAllTranslations();
            // 计算总数
            const totalCount = allRecords.length;
            // 计算唯一词数（基于原文）
            const uniqueWords = new Set(allRecords.map((record) => record.originalText.trim().toLowerCase()));
            const uniqueWordsCount = uniqueWords.size;
            return {
                totalCount,
                uniqueWordsCount,
            };
        }
        catch (error) {
            return {
                totalCount: 0,
                uniqueWordsCount: 0,
            };
        }
    }
    /**
     * 获取翻译记录总数
     * 直接调用 provider 的方法
     */
    async getTranslationCount() {
        await this.ensureInitialized();
        return await this.provider.getTranslationCount();
    }
    /**
     * 在事务中执行操作
     * 直接调用 provider 的事务方法
     */
    async transaction(callback) {
        await this.ensureInitialized();
        return await this.provider.transaction(callback);
    }
}
// 单例实例
TranslationStorageService.instance = null;
//# sourceMappingURL=TranslationStorageService.js.map